import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRODUCT_OPTS = [
  { id: "BB-01", label: "The Ballboy (Ball Holder)" },
  { id: "BB-02", label: "The Marker (Court Numbers)" },
  { id: "CUSTOM", label: "Custom / Other (describe below)" },
];

export default function QuoteForm() {
  const [form, setForm] = useState({
    facility_name: "",
    contact_name: "",
    email: "",
    phone: "",
    num_courts: "",
    products: [],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const toggleProduct = (id) => {
    setForm((s) => ({
      ...s,
      products: s.products.includes(id)
        ? s.products.filter((p) => p !== id)
        : [...s.products, id],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (
      !form.facility_name ||
      !form.contact_name ||
      !form.email ||
      !form.num_courts
    ) {
      toast.error("Please fill in the required fields.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        num_courts: parseInt(form.num_courts, 10),
        phone: form.phone || null,
        message: form.message || null,
      };
      await axios.post(`${API}/quote-requests`, payload);
      setSubmitted(true);
      toast.success("Request received. We'll be in touch within 1 business day.");
      setForm({
        facility_name: "",
        contact_name: "",
        email: "",
        phone: "",
        num_courts: "",
        products: [],
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please email us directly.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="bg-[#0A0A0A] text-white border-b border-[#0A0A0A]"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* Left info */}
        <div className="lg:col-span-5">
          <div className="overline text-[#DFFF00]">06 / Request a Quote</div>
          <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-[56px] leading-[1.02] tracking-tight font-medium">
            Tell us about
            <br />
            your facility.
          </h2>
          <p className="mt-6 text-[15px] text-white/75 leading-relaxed max-w-md">
            Drop us a note with your court count and which parts you're
            interested in. We'll respond within one business day with pricing
            and timelines.
          </p>

          <div className="mt-12 border border-white/15 divide-y divide-white/15">
            <InfoRow label="Email" value="johnleecd@gmail.com" href="mailto:johnleecd@gmail.com" testId="info-email" />
            <InfoRow label="Production" value="USA · On-demand" />
            <InfoRow label="Avg. Response" value="< 24 hours, business days" />
          </div>

          <div className="mt-10 font-mono-bb text-[11px] tracking-widest text-white/50">
            // PREFER EMAIL?{" "}
            <a
              href="mailto:johnleecd@gmail.com"
              data-testid="quote-email-link"
              className="text-[#DFFF00] hover:underline"
            >
              johnleecd@gmail.com
            </a>
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          <div className="bg-white text-[#0A0A0A] p-8 md:p-10 border border-white/10">
            {submitted ? (
              <SubmittedState onReset={() => setSubmitted(false)} />
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="quote-form"
                noValidate
                className="grid grid-cols-1 md:grid-cols-2 gap-5"
              >
                <Field label="Facility Name *" htmlFor="facility_name" full>
                  <Input
                    id="facility_name"
                    data-testid="input-facility-name"
                    placeholder="e.g. Coastal Pickleball Center"
                    value={form.facility_name}
                    onChange={(e) => update("facility_name", e.target.value)}
                    required
                  />
                </Field>

                <Field label="Contact Name *" htmlFor="contact_name">
                  <Input
                    id="contact_name"
                    data-testid="input-contact-name"
                    placeholder="Your full name"
                    value={form.contact_name}
                    onChange={(e) => update("contact_name", e.target.value)}
                    required
                  />
                </Field>

                <Field label="Email *" htmlFor="email">
                  <Input
                    id="email"
                    type="email"
                    data-testid="input-email"
                    placeholder="you@facility.com"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    required
                  />
                </Field>

                <Field label="Phone" htmlFor="phone">
                  <Input
                    id="phone"
                    data-testid="input-phone"
                    placeholder="(555) 123-4567"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                  />
                </Field>

                <Field label="# of Courts *" htmlFor="num_courts">
                  <Input
                    id="num_courts"
                    data-testid="input-num-courts"
                    type="number"
                    min="1"
                    placeholder="e.g. 12"
                    value={form.num_courts}
                    onChange={(e) => update("num_courts", e.target.value)}
                    required
                  />
                </Field>

                <div className="md:col-span-2">
                  <Label className="overline text-[#525252]">Products of Interest</Label>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {PRODUCT_OPTS.map((opt) => {
                      const active = form.products.includes(opt.id);
                      return (
                        <label
                          key={opt.id}
                          data-testid={`product-opt-${opt.id.toLowerCase()}`}
                          className={`cursor-pointer flex items-start gap-3 border p-4 transition-colors ${
                            active
                              ? "border-[#0A0A0A] bg-[#DFFF00]/15"
                              : "border-[#E5E5E5] hover:border-[#0A0A0A]"
                          }`}
                        >
                          <Checkbox
                            checked={active}
                            onCheckedChange={() => toggleProduct(opt.id)}
                            className="mt-0.5"
                          />
                          <div>
                            <div className="font-mono-bb text-[11px] tracking-widest text-[#525252]">
                              {opt.id}
                            </div>
                            <div className="text-[13px] leading-snug">{opt.label}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <Field label="Message" htmlFor="message" full>
                  <Textarea
                    id="message"
                    data-testid="input-message"
                    rows={4}
                    placeholder="Tell us about your timeline, branding, or any custom needs."
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                  />
                </Field>

                <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-2">
                  <p className="font-mono-bb text-[11px] tracking-widest text-[#525252]">
                    // YOUR INFO IS USED ONLY TO RESPOND TO THIS QUOTE.
                  </p>
                  <button
                    type="submit"
                    data-testid="quote-submit-btn"
                    disabled={submitting}
                    className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white px-7 py-3.5 text-[12px] font-semibold tracking-widest uppercase hover:bg-[#DFFF00] hover:text-[#0A0A0A] transition-colors disabled:opacity-60"
                  >
                    {submitting ? "Submitting..." : "Submit Request"}
                    <i className="fa-solid fa-arrow-right text-[11px]" />
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label htmlFor={htmlFor} className="overline text-[#525252]">
        {label}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function InfoRow({ label, value, href, testId }) {
  const inner = (
    <div className="flex items-baseline justify-between gap-4 px-5 py-4">
      <span className="overline text-white/55">{label}</span>
      <span className="font-mono-bb text-[13px] text-white">{value}</span>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testId} className="block hover:bg-white/5 transition-colors">
      {inner}
    </a>
  ) : (
    <div>{inner}</div>
  );
}

function SubmittedState({ onReset }) {
  return (
    <div data-testid="quote-success" className="text-center py-10">
      <div className="inline-flex w-14 h-14 items-center justify-center bg-[#DFFF00] mb-6">
        <i className="fa-solid fa-check text-[#0A0A0A] text-xl" />
      </div>
      <h3 className="font-display text-3xl tracking-tight font-medium">
        Request received.
      </h3>
      <p className="mt-3 text-[15px] text-[#525252] max-w-md mx-auto">
        Thanks for reaching out. We'll respond within one business day at the
        email you provided. For urgent matters, reach us at{" "}
        <a
          href="mailto:johnleecd@gmail.com"
          className="text-[#0A0A0A] underline underline-offset-4"
        >
          johnleecd@gmail.com
        </a>
        .
      </p>
      <button
        data-testid="quote-success-reset"
        onClick={onReset}
        className="mt-8 inline-flex items-center gap-3 border border-[#0A0A0A] px-6 py-3 text-[12px] font-semibold tracking-widest uppercase hover:bg-[#0A0A0A] hover:text-white transition-colors"
      >
        Submit Another Request
      </button>
    </div>
  );
}
